import { Loader } from '@progress/kendo-react-indicators'
import classNames from 'classnames'
import { type ExtractAtomValue, useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import * as R from 'remeda'

import { exists, type SurveyFilterGroups } from '~/utils'

import { type onChangeAtom, propAtom, surveyIdAtom } from './atoms'
import {
  SurveyFilterInformationContent,
  type SurveyFilterInformationContentProps,
} from './components/SurveyFilterInformationContent'
import { useMainQuery } from './hooks/useMainQuery'
import { useSurveyFiltersQuery } from './hooks/useSurveyFiltersQuery'
import styles from './SurveyFilterInformation.css'

export type SurveyFilterInformationProps = {
  className?: string
  'data-testid'?: string
  filterGroups: SurveyFilterGroups | undefined
  onChange?: ExtractAtomValue<typeof onChangeAtom>
  onReady?: SurveyFilterInformationContentProps['onReady']
  sampleSize: number
  surveyId: string
}

export const SurveyFilterInformation = ({
  className,
  'data-testid': dataTestId = 'components-features-survey-filter-information',
  filterGroups,
  onChange,
  onReady,
  sampleSize,
  surveyId: surveyIdProp,
}: SurveyFilterInformationProps) => {
  const { isLoading } = useMainQuery()
  useSurveyFiltersQuery()

  const setProp = useSetAtom(propAtom)

  React.useEffect(() => {
    setProp({ surveyId: surveyIdProp, onChange: onChange ?? R.doNothing })

    return () => {
      setProp({ surveyId: null as unknown as string, onChange: R.doNothing })
    }
  }, [onChange, setProp, surveyIdProp])

  const surveyId = useAtomValue(surveyIdAtom)

  if (isLoading || !exists(surveyId)) {
    return (
      <Loader
        className={classNames(styles.loader, className)}
        data-testid={dataTestId}
      />
    )
  }

  return (
    <SurveyFilterInformationContent
      className={className}
      data-testid={dataTestId}
      filterGroups={filterGroups ?? []}
      sampleSize={sampleSize}
      onReady={onReady}
    />
  )
}
